import React, { useRef, useState, useEffect } from "react";
import {
  Fab,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import SettingsVoiceTwoToneIcon from "@mui/icons-material/SettingsVoiceTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import api from "../api/axios";

/* ------------------ HELPERS ------------------ */

const detectLanguage = (text) => {
  if (/[\u0900-\u097F]/.test(text)) return "hi";
  if (/[\u0980-\u09FF]/.test(text)) return "bn";
  return "en";
};

/* ------------------ COMPONENT ------------------ */

export default function FloatingEtuButton() {
  const [position, setPosition] = useState(() => ({
    x: window.innerWidth - 90,
    y: window.innerHeight - 160,
  }));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [muted, setMuted] = useState(false);

  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const voicesRef = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    const loadVoices = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages]);

  const speakResponse = (text, lang) => {
    if (!window.speechSynthesis) return;
    if (muted) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voices = voicesRef.current || [];

    const preferredVoices = {
      hi: ["Google हिन्दी", "Microsoft Heera"],
      bn: ["Google বাংলা", "Microsoft Tanu"],
      en: ["Google UK English Female", "Google US English"],
    };

    let selectedVoice =
      voices.find((v) =>
        preferredVoices[lang]?.some((name) => v.name.includes(name)),
      ) ||
      voices.find((v) => v.lang.startsWith(lang)) ||
      voices.find((v) => v.lang.startsWith("en"));

    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
    } else {
      utterance.lang = "en-US";
    }

    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    window.speechSynthesis.speak(utterance);
  };

  /* ------------------ DRAG ------------------ */

  const startDrag = (e) => {
    dragging.current = true;
    const p = e.touches ? e.touches[0] : e;
    offset.current = { x: p.clientX - position.x, y: p.clientY - position.y };
  };

  const onDrag = (e) => {
    if (!dragging.current) return;
    const p = e.touches ? e.touches[0] : e;
    setPosition({
      x: p.clientX - offset.current.x,
      y: p.clientY - offset.current.y,
    });
  };

  const endDrag = () => (dragging.current = false);

  /* ------------------ AI LOGIC ------------------ */

  const fakeTypeResponse = (text, lang) => {
    let i = 0;
    setMessages((p) => [...p, { role: "assistant", content: "", lang }]);

    const interval = setInterval(() => {
      i++;
      setMessages((p) => {
        const u = [...p];
        u[u.length - 1].content = text.slice(0, i);
        return u;
      });
      if (i >= text.length) {
        clearInterval(interval);
        speakResponse(text, lang);
      }
    }, 22);
  };

  const askAi = async (question) => {
    const lang = detectLanguage(question);

    setMessages((p) => [...p, { role: "user", content: question, lang }]);
    setThinking(true);

    try {
      const res = await api.post("/etu/ask", {
        query: question,
        language: lang,
      });

      const aiText =
        res?.data?.data || res?.data?.message || "AI response unavailable.";

      setThinking(false);
      fakeTypeResponse(aiText, lang);
    } catch (err) {
      setThinking(false);
      fakeTypeResponse("⚠ AI system temporarily unavailable.", "en");
    }
  };

  /* ------------------ VOICE ------------------ */

  const startVoiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return alert("Voice not supported");

    const recog = new SR();
    recog.lang = "hi-IN";
    recog.onresult = (e) => askAi(e.results[0][0].transcript);
    recog.start();
  };

  const toggleMute = () => {
    setMuted((prev) => {
      if (!prev) {
        // 🔇 Muting now → stop ongoing speech immediately
        window.speechSynthesis.cancel();
      }
      return !prev;
    });
  };

  /* ------------------ UI ------------------ */

  return (
    <>
      {/* CHAT WINDOW */}
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 90,
            right: 20,
            width: 360,
            height: 440,
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",

            /* GLASS + ROBOTIC */
            background:
              "linear-gradient(135deg, rgba(20,30,30,0.75), rgba(5,15,15,0.65))",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(34,197,94,0.4)",
            borderRadius: 3,
            boxShadow:
              "0 0 25px rgba(34,197,94,0.25), inset 0 0 10px rgba(255,255,255,0.05)",
            animation: "fadeIn 0.3s ease-out",

            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(34,197,94,0.3)",
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                color: "#7CFFB2",
                letterSpacing: 1,
              }}
            >
              ETU.AI ▸ ONLINE
            </Typography>

            <Box>
              <IconButton size="small" onClick={toggleMute}>
                {muted ? (
                  <VolumeOffIcon sx={{ color: "#ff6b6b" }} />
                ) : (
                  <VolumeUpIcon sx={{ color: "#7CFFB2" }} />
                )}
              </IconButton>

              <IconButton size="small" onClick={() => setOpen(false)}>
                <CloseIcon sx={{ color: "#7CFFB2" }} />
              </IconButton>
            </Box>
          </Box>

          {/* MESSAGES */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
              fontFamily: "monospace",
            }}
          >
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  bgcolor:
                    m.role === "user"
                      ? "rgba(34,197,94,0.18)"
                      : "rgba(255,255,255,0.06)",
                  border:
                    m.role === "assistant"
                      ? "1px solid rgba(124,255,178,0.25)"
                      : "1px solid rgba(34,197,94,0.4)",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  maxWidth: "82%",
                  color: "#E6FFF1",
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                }}
              >
                {m.content}
              </Box>
            ))}

            {thinking && (
              <Box
                sx={{
                  alignSelf: "flex-start",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: "#7CFFB2",
                  border: "1px dashed rgba(124,255,178,0.4)",
                  background: "rgba(255,255,255,0.04)",
                  animation: "pulse 1.2s infinite ease-in-out",
                  "@keyframes pulse": {
                    "0%": { opacity: 0.4 },
                    "50%": { opacity: 1 },
                    "100%": { opacity: 0.4 },
                  },
                }}
              >
                ETU is thinking
                <span className="dot-anim">...</span>
              </Box>
            )}

            <div ref={bottomRef} />
          </Box>
        </Paper>
      )}

      {/* FLOATING BUTTON */}
      <Box
        sx={{
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: 1500,
        }}
      >
        <Fab
          color="primary"
          onClick={() => {
            setOpen(true);
            startVoiceInput();
          }}
          onMouseDown={startDrag}
          onMouseMove={onDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={startDrag}
          onTouchMove={onDrag}
          onTouchEnd={endDrag}
          sx={{
            boxShadow: "0 0 18px rgba(34,197,94,0.7)",
          }}
        >
          <Box
            sx={{
              animation: "beat 1.6s infinite",
              "@keyframes beat": {
                "0%": { transform: "scale(1)" },
                "10%": { transform: "scale(1.15)" },
                "20%": { transform: "scale(1)" },
              },
            }}
          >
            <SettingsVoiceTwoToneIcon />
          </Box>
        </Fab>
      </Box>
    </>
  );
}
